import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LCSubPageLayout from './LCSubPageLayout';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getChapterBylaws,
  createOrUpdateChapterBylaws,
  deleteChapterBylaws,
} from '../../../endpoints/chaptersTs';
import type { ByLawsResponse, ByLawsSection } from '../../../types/chapters';
import { BylawsEditor } from './BylawsEditor';
import { LIST_CONTENT_TYPE_ID } from './bylawsEditorConstants';

const LCBylawsPage: React.FC = () => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <LCSubPageLayout title="Bylaws">
      {(detail) => (
        <LCBylawsContent
          chapterId={detail.id}
          canEditBylaws={detail.can_edit_bylaws ?? false}
          deleteConfirmOpen={deleteConfirmOpen}
          setDeleteConfirmOpen={setDeleteConfirmOpen}
          isDeleting={isDeleting}
          setIsDeleting={setIsDeleting}
        />
      )}
    </LCSubPageLayout>
  );
};

interface LCBylawsContentProps {
  chapterId: string;
  canEditBylaws: boolean;
  deleteConfirmOpen: boolean;
  setDeleteConfirmOpen: (v: boolean) => void;
  isDeleting: boolean;
  setIsDeleting: (v: boolean) => void;
}

const LCBylawsContent: React.FC<LCBylawsContentProps> = ({
  chapterId,
  canEditBylaws,
  deleteConfirmOpen,
  setDeleteConfirmOpen,
  isDeleting,
  setIsDeleting,
}) => {
  const {
    data: bylaws,
    isLoading,
    refetch,
  } = useApiGet(
    ['chapter-bylaws', chapterId],
    () => getChapterBylaws(chapterId),
    { enabled: !!chapterId }
  );

  const { mutate: saveBylaws, isPending: isSaving } = useApiSend(
    () => createOrUpdateChapterBylaws(chapterId, bylawsPayload),
    () => {
      refetch();
      setEditMode(false);
    },
    undefined,
    [['chapter-bylaws', chapterId]]
  );

  const [editMode, setEditMode] = useState(false);
  const [bylawsPayload, setBylawsPayload] = useState<ByLawsResponse>({
    sections: [],
  });

  const isEmpty = !bylaws?.id || !bylaws.sections?.length;

  const handleCreate = () => {
    setBylawsPayload({ id: undefined, sections: [] });
    setEditMode(true);
  };

  const handleEdit = () => {
    setBylawsPayload({
      id: bylaws?.id ?? undefined,
      sections: bylaws?.sections ?? [],
    });
    setEditMode(true);
  };

  const handleSave = () => {
    saveBylaws(undefined as any);
  };

  const handleDeleteClick = () => setDeleteConfirmOpen(true);
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteChapterBylaws(chapterId);
      refetch();
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <Typography color="text.secondary">Loading bylaws...</Typography>;
  }

  if (editMode) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={() => setEditMode(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Box>
        <BylawsEditor payload={bylawsPayload} onChange={setBylawsPayload} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {isEmpty ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No bylaws have been added for this chapter.
          </Typography>
          {canEditBylaws && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ mt: 2 }}
            >
              Add Bylaws
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {canEditBylaws && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Delete Bylaws
              </Button>
            </Box>
          )}
          <BylawsView sections={bylaws?.sections ?? []} />
        </>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete chapter bylaws?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all bylaws content for this chapter. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function BylawsView({ sections }: { sections: ByLawsSection[] }) {
  if (!sections?.length) return null;

  return (
    <Box>
      {sections
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <Paper key={section.id} sx={{ p: 2, mb: 2 }} variant="outlined">
            <Typography variant="h6" gutterBottom>
              {section.title || `Section ${section.order}`}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {(section.content ?? [])
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((content) => (
                <Box key={content.id} sx={{ mb: 2 }}>
                  {content.content_type_id !== LIST_CONTENT_TYPE_ID &&
                    content.title && (
                      <Typography variant="subtitle2" fontWeight="bold">
                        {content.title}
                      </Typography>
                    )}
                  {content.paragraph && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {content.paragraph}
                    </Typography>
                  )}
                  {content.content_type_id === LIST_CONTENT_TYPE_ID &&
                    content.list?.length > 0 && (
                      <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                        {content.list
                          .slice()
                          .sort((a, b) => a.order - b.order)
                          .map((item) => (
                            <li key={item.id}>
                              <Typography variant="body2">
                                {item.content}
                              </Typography>
                            </li>
                          ))}
                      </Box>
                    )}
                </Box>
              ))}
          </Paper>
        ))}
    </Box>
  );
}

export default LCBylawsPage;
